import { useCallback, useEffect, useState } from "react";
import { DocumentContainerType } from "src/enums/Documents";
import { Documents } from "src/interfaces/Documents";
import { handleUserUpdate } from "src/redux/redux-store";
import { useAppDispatch, useAppSelector } from "src/redux/redux-store/hooks";
import { ResponseKind } from "src/services/services/MainService";
import { adminService } from "src/services/services/admin/admin.api";
import { useAuth } from "./useAuth";
import {
  sendFailureNotification,
  sendSuccessNotification,
} from "src/utils/notifications";
import {
  FileUploadMap,
  FileUrlMap,
  KnowledgeBaseDocumentType,
} from "src/interfaces/KnowledgeBase";
import { knowledgeBaseService } from "src/services/services/knowledgeBase/knowledgeBase.api";
import { NotificationTypes } from "src/enums/Notifications";
import { FetchingCombinedSummary } from "src/interfaces/KnowledgeBase";

export const useKnowledgebase = (autoFetchFiles = true) => {
  const dispatch = useAppDispatch();
  const container = DocumentContainerType.knowledgebase;
  const azure_token = useAppSelector((state) => state.user.azureStorageToken);
  const email = useAppSelector((state) => state.user.email);
  const { token } = useAuth();

  const [loading, setLoading] = useState(false);
  const [fetchingCombinedSummary, setFetchingCombinedSummary] =
    useState<boolean>(false);
  const [deleting, setDeleting] = useState(false);

  const [uploadFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadQueue, setUploadQueue] = useState<Documents[]>([]);

  const [files, setFiles] = useState<KnowledgeBaseDocumentType[]>([]);
  const [failFiles, setFailFiles] = useState<Documents[]>([]);
  const [azureUrls, setAzureUrls] = useState<FileUrlMap | null>(null);

  const [error, setError] = useState("");

  useEffect(() => {
    if (token && autoFetchFiles) {
      // getAzureToken();
      getDocs();
    }
  }, [token, autoFetchFiles]);

  useEffect(() => {
    if (!email) {
      dispatch(handleUserUpdate());
    }
    if (uploadFiles?.length > 0 && azure_token && email) {
      // uploadDoc(azure_token);
    }
  }, [uploadFiles, azure_token, email]);

  useEffect(() => {
    if (failFiles?.length > 0) {
      for (let i = failFiles.length - 1; i >= 0; i--) {
        let item = failFiles[i];
        sendFailureNotification(
          NotificationTypes.fileUpload,
          `${item.filename} was not uploaded`
        );
      }
    }
  }, [failFiles]);

  const transformFilesToQueueFiles = (files: File[]) => {
    const temp: Documents[] = files?.map((item, index) => {
      return {
        filename: item.name,
        content_type: item.type,
        status: "Uploading",
        id: index,
        error: "",
      };
    });

    return temp;
  };

  // const getAzureToken = useCallback(async () => {
  //   const response = await adminService.getUserTokenKnowledgebase();

  //   if (response.kind == ResponseKind.OK && response.data) {
  //     dispatch(updateAzureToken(response.data?.azure_storage_token));
  //   }
  // }, []);

  const clearQueue = useCallback(() => {
    setUploadQueue((prev) => []);
    getDocs();
  }, []);

  const dismissError = useCallback(() => {
    setError("");
  }, []);

  // const uploadDoc = async (azure_token: string) => {
  //   const validationData = [...uploadFiles];
  //   const fileNames = validationData.map((item) => item.name);
  //   let filesToUpload: File[] = [];

  //   try {
  //     const validate_file_names = await documentService.validateFiles(
  //       fileNames)

  //     if (uploadFiles?.length > 200) {
  //       setError("You can upload max 200 files at one time");

  //       return;
  //     }

  //     if (validate_file_names.kind == ResponseKind.OK) {
  //       if (validate_file_names.data) {
  //         validate_file_names.data.files.forEach((item) => {
  //           if (item.exists) {
  //             sendFailureNotification(NotificationTypes.fileUpload, `File ${item.filename} already exists`)
  //             return;
  //           } else {
  //             let fileIndex = uploadFiles.findIndex(
  //               (i) => i.name == item.filename);
  //             filesToUpload.push(uploadFiles[fileIndex]);
  //           }
  //         });

  //         const queue = transformFilesToQueueFiles(filesToUpload);
  //         setUploadQueue((prev) => [...prev, ...queue]);
  //         // processQueue(queue);

  //         if(filesToUpload?.length > 0){
  //             const failed = await adminService.uploadDoc(
  //             filesToUpload,
  //             email,
  //             azure_token,
  //             container
  //           );
  //           console.log("Files that didn't upload", failed);
  //           if(failed && failed?.length > 0){
  //             // setUploadQueue([]);

  //             setError("A network issue was encounterd! Some files were not uploaded")
  //           }}

  //       }
  //     } else {
  //       throw "File names could not be validates";
  //     }
  //   } catch (error) {
  //     setError(`${error}`);
  //   } finally {
  //     getDocs();
  //     setUploadedFiles([]);
  //   }
  // };

  // const processDeletion = (fileNames: string[]) => {
  //   const updatedFiles = files.filter(
  //     (file) => !fileNames.includes(file.filename)
  //   );
  //   setFiles(updatedFiles);
  //
  // }

  const getDocs = async () => {
    try {
      setLoading(true);
      const response = await knowledgeBaseService.getFilesList();
      if (response.kind == ResponseKind.OK) {
        const filesData = Array.isArray(response.data) ? response.data : [];
        setFiles(filesData);
      } else {
        console.error("Failed to fetch files:", response);
        setFiles([]);
      }
    } catch (e) {
      console.error("Error fetching files:", e);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (name: string) => {
    const response = await adminService.downloadKnowledgebaseDoc(name);
    if (response.kind == ResponseKind.OK) {
      if (response.data) {
        fetch(response?.data?.download_link).then((response) => {
          response.blob().then((blob) => {
            // Creating new object of PDF file
            const fileURL = window.URL.createObjectURL(blob);

            // Setting various property values
            let alink = document.createElement("a");
            alink.href = fileURL;
            alink.download = name || "fileName";
            alink.click();
          });
        });
      }
    }
  };

  const deleteSingleFile = async (
    file_name: string,
    subject: string,
    is_qb: boolean = false
  ) => {
    try {
      setLoading(true);
      const response = await knowledgeBaseService.deleteFile(
        file_name,
        subject,
        is_qb
      );
      if (response.kind == ResponseKind.OK) {
        sendSuccessNotification(
          NotificationTypes.fileDeletion,
          "File deleted successfully!"
        );
        getDocs();
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.fileDeletion,
        "File deletion failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // const deleteFiles = async () => {
  //   if (selectedFiles?.length > 0) {
  //     const fileNamesToDelete = selectedFiles.map((item)=> item.filename);
  //     const deletedFiles = files.reduce((acc: Documents[], file) => {
  //       if (fileNamesToDelete.includes(file.filename)) {
  //         // Push a new object with updated status to the accumulator
  //         acc.push({ ...file, status: 'Deleting' });
  //       }
  //       return acc;
  //     }, []);
  //     setUploadQueue(deletedFiles)
  //     const response = await adminService.deleteKnowledgebaseDocs(Object.keys(selectedFiles));

  //     if (response.kind == ResponseKind.OK) {

  //       processDeletion(fileNamesToDelete)
  //     } else {
  //       // Optionally handle API errors
  //       console.error("Failed to delete files:");
  //     }
  //   }
  // };

  const getCombinedSummary = async (
    level: string | null | undefined,
    subject: string | null | undefined
  ): Promise<FetchingCombinedSummary> => {
    if (!level || !subject) {
      return {
        isFetching: false,
        error: "",
        data: null,
      };
    }
    try {
      setFetchingCombinedSummary(true);
      const response = await knowledgeBaseService.getCombinedSummary(
        level,
        subject
      );
      if (response.kind === "ok") {
        return {
          isFetching: false,
          error: "",
          data: response.data,
        };
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      let msg = "Failed to load combined summary";
      if (err instanceof Error && err.message.includes("404")) {
        msg = "No KB summaries found for the selected level and subject";
      }
      setError(msg);
      return {
        isFetching: false,
        error: msg,
        data: null,
      };
    } finally {
      setFetchingCombinedSummary(false);
    }
  };

  const getUrlsToPostOnAzure = async (
    fileName: string,
    fileIds: string[]
  ): Promise<FileUrlMap | undefined> => {
    try {
      setLoading(true);
      const response = await knowledgeBaseService.getUrlsToPostOnAzure(
        fileName,
        fileIds
      );
      if (response.kind === "ok") {
        setAzureUrls(response.data);
        return response.data;
      }
      throw new Error("Failed to get urls to post on Azure");
    } catch (error) {
      console.error("Error getting urls to post on Azure", error);
      setError("Error getting urls to post on Azure");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const uploadFilesToAzure = async (
    files: FileUploadMap
  ): Promise<{ kind: "ok" } | { kind: "bad-data" }> => {
    try {
      setLoading(true);
      const response = await knowledgeBaseService.uploadFilesToAzure(files);
      if (response.kind === "ok") {
        return { kind: "ok" };
      }
      throw new Error("Failed to upload files to Azure");
    } catch (error) {
      console.error("Error uploading files to Azure", error);
      setError("Error uploading files to Azure");
      return { kind: "bad-data" };
    } finally {
      setLoading(false);
    }
  };

  const postQuestionBankPreparedData = async ({
    is_qb,
    level,
    subject,
    summary,
    file_name,
  }: {
    is_qb: boolean;
    level: string;
    subject: string;
    summary: string;
    file_name: string;
  }) => {
    try {
      setLoading(true);
      const response = await knowledgeBaseService.uploadManualFile(
        is_qb,
        level,
        subject,
        summary,
        file_name,
        false
      );
      if (response.kind === "ok") {
        return { kind: "ok" };
      }
      throw new Error(
        response.message || "Failed to send question bank prepared data"
      );
    } catch (error) {
      console.error("Error sending question bank prepared data", error);
      if (error instanceof Error) {
        setError(error.message || "Error sending question bank prepared data");
      } else {
        setError("Error sending question bank prepared data");
      }
      return {
        kind: "bad-data",
        message:
          error instanceof Error
            ? error.message
            : "Error sending question bank prepared data",
      };
    } finally {
      setLoading(false);
    }
  };

  const updateManualQuestionBank = async (
    is_qb: boolean,
    level: string,
    subject: string,
    summary: string,
    file_name: string
  ) => {
    try {
      setLoading(true);
      const response = await knowledgeBaseService.modifyFile(
        is_qb,
        level,
        subject,
        summary,
        file_name
      );
      if (response.kind === "ok") {
        return { kind: "ok" };
      }
      throw new Error("Failed to update question bank");
    } catch (error) {
      console.error("Error updating question bank", error);
      setError("Error updating question bank");
      return { kind: "bad-data" };
    } finally {
      setLoading(false);
    }
  };

  return {
    downloadFile,
    error,
    loading,
    failFiles,
    uploadQueue,
    clearQueue,
    getDocs,
    setUploadQueue,
    dismissError,
    deleteSingleFile,
    transformFilesToQueueFiles,
    files,
    uploadFiles,
    getCombinedSummary,
    setUploadedFiles,
    getUrlsToPostOnAzure,
    fetchingCombinedSummary,
    azureUrls,
    uploadFilesToAzure,
    postQuestionBankPreparedData,
    updateManualQuestionBank,
  };
};
